import { config } from '@/config';
import { useAuth } from './auth';

export const useEmbeddings = () => {
    const { getAuthHeaders } = useAuth();

    /**
     * Get embedding statistics (coverage, total posts, etc.)
     * @returns {Promise<Object>} Embedding stats
     */
    const getEmbeddingStats = async () => {
        try {
            const response = await fetch(
                `${config.API_BASE_URL}/api/v1/admin/embeddings/stats`,
                {
                    headers: getAuthHeaders(),
                }
            );
            if (response.ok) {
                return await response.json();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch embedding stats');
            }
        } catch (error) {
            console.error('Error fetching embedding stats:', error);
            throw error;
        }
    };

    /**
     * Batch generate embeddings for published posts
     * @param {Object} options - Generation options
     * @param {boolean} options.force_regenerate - Force regenerate existing embeddings
     * @param {Array<string>} options.post_slugs - Optional array of specific post slugs (empty = all posts)
     * @returns {Promise<Object>} Batch generation result
     */
    const batchGenerateEmbeddings = async (options = {}) => {
        try {
            const response = await fetch(
                `${config.API_BASE_URL}/api/v1/admin/embeddings/generate-batch`,
                {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        force_regenerate: options.force_regenerate || false,
                        post_slugs: options.post_slugs || [],
                    }),
                }
            );

            // Backend returns 202 Accepted with acknowledgement message
            if (response.status === 202) {
                const data = await response.json();
                return {
                    acknowledged: true,
                    message: data.message || data.status || 'Batch generation started',
                };
            }

            if (response.ok) {
                return await response.json();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate embeddings');
            }
        } catch (error) {
            console.error('Error generating embeddings:', error);
            throw error;
        }
    };

    return {
        getEmbeddingStats,
        batchGenerateEmbeddings,
    };
};
